import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { inviteMemberAgent, listMemberAgents, listOrgAgents, removeMemberAgent } from './client';
import { keys } from './keys';

/**
 * Custom agents (ADR-0080). The org's shared agents change only when one is
 * seeded or approved, so they hold a long stale window; a workstream's member
 * list changes when someone invites or removes, which the mutations below
 * invalidate. The server re-checks tenant and ownership on every call — the SPA
 * only shows what it is given.
 */
export function useOrgAgents() {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.orgAgents(),
    queryFn: ({ signal }) => listOrgAgents({ getAccessToken, signal }),
    staleTime: 5 * 60_000,
    select: (response) => response.data.agents,
  });
}

/** This workstream's member agents — `null` before the workstream exists. */
export function useMemberAgents(workstreamId: string | null) {
  const { getAccessToken } = useAuth();
  return useQuery({
    queryKey: keys.workstream.memberAgents(workstreamId ?? ''),
    queryFn: ({ signal }) => listMemberAgents(workstreamId as string, { getAccessToken, signal }),
    enabled: workstreamId !== null,
    select: (response) => response.data.members,
  });
}

/** Invite one of the org's shared agents. Idempotent server-side. */
export function useInviteMemberAgent(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.workstream.inviteAgent(workstreamId),
    mutationFn: (agentId: string) => inviteMemberAgent(workstreamId, agentId, { getAccessToken }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workstream.memberAgents(workstreamId) }),
  });
}

/** Remove a member agent — the off switch. Idempotent server-side. */
export function useRemoveMemberAgent(workstreamId: string) {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: keys.workstream.removeAgent(workstreamId),
    mutationFn: (agentId: string) => removeMemberAgent(workstreamId, agentId, { getAccessToken }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.workstream.memberAgents(workstreamId) }),
  });
}
